import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Grid,
  Stack,
  Table,
  Button,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  TextField,
  Typography,
  InputAdornment,
  TableContainer,
} from "@mui/material";
import moment from "moment";
import "../Style/pagestyle.scss";
import { useQuery } from "@apollo/client";
import CreateIcon from "../Assets/create-icon.svg";
import SearchIcon from "@mui/icons-material/Search";
import { padStartINV } from "../Function/DynamicFn";
import { AuthContext } from "../Context/AuthContext";
import EmptyData from "../Components/Include/EmptyData";
import { translateLauguage } from "../Function/Translate";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import LoadingPage from "../Components/Include/LoadingPage";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import FooterPagination from "../Components/Include/FooterPagination";
import { GET_GENERAL_INVOICE_PAGINATION } from "../Schema/Generalincome";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import CreateGeneralIncome from "../Components/GeneralIncome/CreateGeneralIncome";
import GeneralIncomeAction from "../Components/GeneralIncome/Invoice/GeneralIncomeAction";

export default function GeneralIncome() {
  // Change Language
  const { language } = useContext(AuthContext);
  const { t } = translateLauguage(language);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [toDate, setToDate] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [keyword, setKeyword] = useState("");
  const [invoiceId, setInvoiceId] = useState();
  const [status, setStatus] = useState("All");
  const [tableData, setTableData] = useState([]);
  const [paginationData, setPaginationData] = useState([]);

  const { refetch } = useQuery(GET_GENERAL_INVOICE_PAGINATION, {
    variables: {
      page,
      limit,
      pagination: true,
      keyword,
      paymentStatus: status === "All" ? null : status,
      endDate: toDate ? moment(toDate).format("YYYY-MM-DD") : null,
      startDate: fromDate ? moment(fromDate).format("YYYY-MM-DD") : null,
      invoiceId: invoiceId ? parseInt(invoiceId) : null,
    },
    onCompleted: ({ getGeneralInvoicePagination }) => {
      // console.log("getGeneralInvoicePagination::", getGeneralInvoicePagination)
      setTableData(getGeneralInvoicePagination?.data || []);
      setPaginationData(getGeneralInvoicePagination?.paginator);
      setLoading(false);
    },
    onError: (err) => {
      console.log("Error::", err?.message);
    },
  });

  const handleLimit = (e) => {
    setLimit(e.target.value);
    setPage(1);
  };

  const handleSearchInv = (e) => {
    setPage(1);
    setInvoiceId(e.target.value);
  };

  const handleSearch = (e) => {
    setPage(1);
    setKeyword(e.target.value);
  };

  useEffect(() => {
    refetch();
  }, [page, keyword]);

  // ======================= Resize width Screen ======================
  const [width, setWidth] = useState(window.innerWidth);
  const updateDimensions = () => {
    setWidth(window.innerWidth - 50);
  };
  useEffect(() => {
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  return (
    <div className="page-container">
      <Stack direction="row" spacing={2} className="page-header">
        <Stack direction="column" justifyContent="center">
          <Box className="slash" />
        </Stack>
        <Stack direction="column" justifyContent="center">
          <Typography className="page-title">General Receipt </Typography>
        </Stack>
      </Stack>

      <Box sx={{ marginTop: "30px" }}>
        <Grid container spacing={3}>
          <Grid item xs={6} sm={4} md={3} lg={1.5} xl={1.5}>
            <Typography className="search-head-title">Search</Typography>
            <TextField
              placeholder="Inv ID"
              size="small"
              type="number"
              className="search-field"
              onChange={(e) => handleSearchInv(e)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 1 }}>
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
            <Typography className="search-head-title">Search</Typography>
            <TextField
              placeholder="Search"
              size="small"
              className="search-field"
              onChange={(e) => handleSearch(e)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 1 }}>
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
            <Typography className="search-head-title">From</Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={fromDate}
                inputFormat="dd-MM-yyyy"
                className="select-date"
                onChange={(newValue) => {
                  setLoading(true);
                  setFromDate(newValue);
                }}
                renderInput={(params) => (
                  <TextField {...params} size="small" fullWidth />
                )}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
            <Typography className="search-head-title">To</Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={toDate}
                inputFormat="dd-MM-yyyy"
                className="select-date"
                onChange={(newValue) => {
                  setLoading(true);
                  setToDate(newValue);
                }}
                renderInput={(params) => (
                  <TextField {...params} size="small" fullWidth />
                )}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6} md={6} lg={12} xl={3.3}>
            <Stack direction="row" spacing={2} justifyContent="right">
              <Button
                onClick={handleOpen}
                className="btn-export"
                startIcon={
                  <img src={CreateIcon} alt="CreateIcon" className="icon-add" />
                }
              >
                {t("create")}
              </Button>
              <CreateGeneralIncome
                open={open}
                setRefetch={refetch}
                handleClose={handleClose}
              />
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <Box className="body-container">
        <TableContainer sx={{ maxWidth: `${width}px` }}>
          <Table className="table">
            <TableHead className="header-row">
              <TableRow>
                <TableCell className="header-title-start">N°</TableCell>
                <TableCell className="header-title">date</TableCell>
                <TableCell className="header-title">Bill To</TableCell>
                <TableCell className="header-title">Amount</TableCell>
                <TableCell className="header-title-end"></TableCell>
              </TableRow>
            </TableHead>
            {loading ? (
              <TableBody className="body">
                <TableRow className="body-row">
                  <TableCell colSpan={5} className="body-cell">
                    <LoadingPage />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <>
                {tableData?.length === 0 ? (
                  <TableBody className="body">
                    <TableRow className="body-row">
                      <TableCell colSpan={5} className="body-cell">
                        <EmptyData />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : (
                  <TableBody className="body" >
                    {tableData?.map((row, index) => {
                      return (
                        <TableRow className={"body-row"} key={index} >
                          <TableCell className="body-cell-start" sx={{ color: "#0f81c2 !important" }} >
                            {padStartINV(row?.invoiceNo)}
                          </TableCell>
                          <TableCell className="body-cell" >
                            {moment(row?.createdAt).format("DD-MMM-YYYY")}
                          </TableCell>
                          <TableCell className="body-cell" >
                            {row?.billTo?.customerNameKhmer}
                          </TableCell>
                          <TableCell className="body-cell" >
                            $ {row?.totalAmount || 0}
                          </TableCell>
                          <TableCell className="body-cell-end">
                            <GeneralIncomeAction
                              editData={row}
                              setRefetch={refetch}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                )}
              </>
            )}
          </Table>
        </TableContainer>
        {/* ==================================== Pagination ============================== */}
        <FooterPagination
          totalPages={paginationData?.totalPages}
          totalDocs={paginationData?.totalDocs}
          limit={limit}
          page={page}
          setPage={setPage}
          handleLimit={handleLimit}
          setLoading={setLoading}
          Type="student"
        />
      </Box>
    </div>
  );
}
